/**
 * Admin Users API Client
 * 
 * Functions for admin user management operations
 */

import { edgeInvoke, edgeGet } from '@/lib/edgeInvoke';

export interface UserWithAdminDetails {
  id: string;
  user_id: string;
  email: string;
  full_name: string;
  created_at: string;
  last_sign_in_at: string | null;
  training_plan_id: string | null;
  swings_analyzed: number;
  analysis_v3_enabled?: boolean;
  coaching_v3_enabled?: boolean;
  snapshot_v2_enabled?: boolean;
  range_mode_enabled?: boolean;
}

export interface UsersListResponse {
  ok: boolean;
  users: UserWithAdminDetails[];
  total: number;
}

export interface ResetPasswordResponse {
  ok: boolean;
  message: string;
  user_id: string;
  user_email: string;
}

export interface UpdateTrainingPlanResponse {
  ok: boolean;
  message: string;
  user_id: string;
  training_plan: {
    id: string;
    primary_track: string;
    skill_level: string;
    created_at: string;
  };
  profile: {
    id: string;
    user_id: string;
    active_training_plan_id: string | null;
  };
}

export interface TrainingPlan {
  id: string;
  primary_track: string;
  skill_level: string;
  created_at: string;
  user_id: string;
}

export interface TrainingPlansListResponse {
  ok: boolean;
  training_plans: TrainingPlan[];
  total: number;
}

/**
 * Fetch all users with admin details
 */
export async function getUsersWithAdminDetails(): Promise<UserWithAdminDetails[]> {
  const response = await edgeGet<UsersListResponse>('admin-users-list');
  if (!response.ok) {
    throw new Error('Failed to fetch users');
  }
  return response.users;
}

/**
 * Reset a user's password
 */
export async function resetUserPassword(
  userId: string,
  newPassword: string
): Promise<ResetPasswordResponse> {
  const response = await edgeInvoke<ResetPasswordResponse>('admin-reset-password', {
    user_id: userId,
    new_password: newPassword,
  });
  return response;
}

/**
 * Update a user's training plan
 */
export async function updateUserTrainingPlan(
  userId: string,
  planId: string
): Promise<UpdateTrainingPlanResponse> {
  const response = await edgeInvoke<UpdateTrainingPlanResponse>('admin-update-training-plan', {
    user_id: userId,
    new_plan_id: planId,
  });
  return response;
}

/**
 * Fetch all training plans (admin only)
 */
export async function getAllTrainingPlans(): Promise<TrainingPlan[]> {
  const response = await edgeGet<TrainingPlansListResponse>('admin-training-plans-list');
  if (!response.ok) {
    throw new Error('Failed to fetch training plans');
  }
  return response.training_plans;
}

export interface UpdateV3FlagsResponse {
  ok: boolean;
  message: string;
  user_id: string;
  flags: {
    analysis_v3_enabled?: boolean;
    coaching_v3_enabled?: boolean;
    snapshot_v2_enabled?: boolean;
  };
}

/**
 * Update per-user V3 feature flags
 */
export async function updateUserV3Flags(
  variables: { userId: string; flags: { analysis_v3_enabled?: boolean; coaching_v3_enabled?: boolean; snapshot_v2_enabled?: boolean } }
): Promise<UpdateV3FlagsResponse> {
  const { userId, flags } = variables;
  const requestBody = {
    user_id: userId,
    ...flags
  };
  const response = await edgeInvoke<UpdateV3FlagsResponse>('admin-update-v3-flags', requestBody);
  return response;
}

